import type { PhoneConnectionOut } from '@biasafe.models';

/**
 * What the Settings card should say about the firm's phone connection.
 *
 * THE SAME RULE AS `mailboxHealth.ts`: say what is true and what to do, and never
 * print `last_error`. It is sanitised on the server but it is still a third
 * party's failure rendered as a string, and the card's only use for it is to tell
 * a declined consent from a failing sync.
 *
 * PENDING IS NOT A FAILURE. The row exists while a Microsoft 365 administrator
 * has been sent the consent link and has not answered, which can be hours and is
 * often somebody else. It reads as waiting, with a way to send the link again.
 */
export type PhoneHealth =
  | { kind: 'ok' }
  | {
      kind: 'pending' | 'revoked' | 'error';
      message: string;
      /** Whether pressing Connect again could fix it. */
      reconnect: boolean;
    };

type Connection = Pick<PhoneConnectionOut, 'status' | 'lastError'>;

const DECLINED = /consent (declined|was not granted)|no tenant/i;

export function phoneHealth(connection: Connection): PhoneHealth {
  switch (connection.status) {
    case 'CONNECTED':
      return { kind: 'ok' };
    case 'PENDING_CONSENT':
      return {
        kind: 'pending',
        message: DECLINED.test(connection.lastError ?? '')
          ? 'Microsoft did not grant access. Ask a Microsoft 365 global administrator to approve the request, then connect again.'
          : 'Waiting for a Microsoft 365 administrator to approve access. Connect again to restart the approval.',
        reconnect: true,
      };
    case 'REVOKED':
      return {
        kind: 'revoked',
        message:
          'Microsoft says access was removed for this firm. Connect again to resume.',
        reconnect: true,
      };
    default:
      return {
        kind: 'error',
        message:
          'The last check with Microsoft failed. It retries on its own; connect again if it keeps failing.',
        reconnect: true,
      };
  }
}
